import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { apiClient } from './client';
import type {
  Bewerber,
  CreateBewerberDto,
  UpdateBewerberDto,
  AssignBewerberDto,
  QueryBewerberDto,
  ApiResponse,
  BewerberStatistics,
  Dokument,
  DokumentTyp,
  AbsageBewerberDto,
  HardDeleteBewerberDto,
  AbsageResponse,
  NichtGeeignetDto,
  MatchingBedarfeParams,
  MatchingBedarfeResponse,
} from '../types/bewerber';
import { activityKeys } from './activities';

// Query keys
export const bewerberKeys = {
  all: ['bewerber'] as const,
  lists: () => [...bewerberKeys.all, 'list'] as const,
  list: (filters: QueryBewerberDto) => [...bewerberKeys.lists(), filters] as const,
  kanban: () => [...bewerberKeys.all, 'kanban'] as const,
  details: () => [...bewerberKeys.all, 'detail'] as const,
  detail: (id: string) => [...bewerberKeys.details(), id] as const,
  statistics: () => [...bewerberKeys.all, 'statistics'] as const,
  documents: (bewerberId: string) => [...bewerberKeys.all, 'documents', bewerberId] as const,
  documentPreview: (bewerberId: string, documentId: string) =>
    [...bewerberKeys.all, 'documentPreview', bewerberId, documentId] as const,
  hasInvitations: (bewerberId: string) =>
    [...bewerberKeys.all, 'hasInvitations', bewerberId] as const,
  // Two levels: invalidating after a Fahrzeit call has to hit every parameter
  // variant, not just the one currently on screen.
  matchingBedarfeAll: (bewerberId: string) =>
    [...bewerberKeys.all, 'matching-bedarfe', bewerberId] as const,
  matchingBedarfe: (bewerberId: string, params: MatchingBedarfeParams) =>
    [...bewerberKeys.matchingBedarfeAll(bewerberId), params] as const,
};

// API functions
const fetchBewerberList = async (query: QueryBewerberDto): Promise<ApiResponse<Bewerber[]>> => {
  const params = new URLSearchParams();
  if (query.search) params.append('search', query.search);
  if (query.status) params.append('status', query.status);
  if (query.city) params.append('city', query.city);
  if (query.ortStelle) params.append('ortStelle', query.ortStelle);
  if (query.assignedUserId) params.append('assignedUserId', query.assignedUserId);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);

  const response = await apiClient.get(`/bewerber?${params.toString()}`);
  return response.data;
};

const fetchBewerberKanban = async (): Promise<ApiResponse<Bewerber[]>> => {
  // Fetch all bewerber for Kanban - VERLOREN column shows last 10 with "view all" link
  // Using high limit to ensure all Bewerber are displayed (was 500, causing older records to be excluded)
  const response = await apiClient.get('/bewerber?limit=10000&sortBy=statusChangedAt&sortOrder=desc');
  return response.data;
};

const fetchBewerber = async (id: string): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.get(`/bewerber/${id}`);
  return response.data;
};

const fetchBewerberStatistics = async (): Promise<ApiResponse<BewerberStatistics>> => {
  const response = await apiClient.get('/bewerber/statistics');
  return response.data;
};

const createBewerber = async (data: CreateBewerberDto): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post('/bewerber', data);
  return response.data;
};

const updateBewerber = async ({ id, data }: { id: string; data: UpdateBewerberDto }): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.patch(`/bewerber/${id}`, data);
  return response.data;
};

const deleteBewerber = async (id: string): Promise<void> => {
  await apiClient.delete(`/bewerber/${id}`);
};

const assignBewerber = async ({
  id,
  data,
}: {
  id: string;
  data: AssignBewerberDto;
}): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/assign`, data);
  return response.data;
};

const checkHasInvitationEmails = async (
  bewerberId: string,
): Promise<ApiResponse<{ hasInvitations: boolean }>> => {
  const response = await apiClient.get(`/bewerber/${bewerberId}/has-invitation-emails`);
  return response.data;
};

const fetchBewerberDocuments = async (bewerberId: string): Promise<ApiResponse<Dokument[]>> => {
  const response = await apiClient.get(`/bewerber/${bewerberId}/documents`);
  return response.data;
};

const uploadDocument = async ({
  bewerberId,
  file,
  typ,
}: {
  bewerberId: string;
  file: File;
  typ: DokumentTyp;
}): Promise<ApiResponse<Dokument>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('typ', typ);

  const response = await apiClient.post(`/bewerber/${bewerberId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

const deleteDocument = async ({
  bewerberId,
  documentId,
}: {
  bewerberId: string;
  documentId: string;
}): Promise<void> => {
  await apiClient.delete(`/bewerber/${bewerberId}/documents/${documentId}`);
};

const downloadDocument = async ({
  bewerberId,
  documentId,
  filename,
}: {
  bewerberId: string;
  documentId: string;
  filename: string;
}): Promise<void> => {
  const response = await apiClient.get(`/bewerber/${bewerberId}/documents/${documentId}`, {
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// React Query Hooks

export function useBewerberList(query: QueryBewerberDto = {}) {
  return useQuery({
    queryKey: bewerberKeys.list(query),
    queryFn: () => fetchBewerberList(query),
  });
}

export function useBewerberKanban() {
  return useQuery({
    queryKey: bewerberKeys.kanban(),
    queryFn: fetchBewerberKanban,
  });
}

export function useBewerber(id: string) {
  return useQuery({
    queryKey: bewerberKeys.detail(id),
    queryFn: () => fetchBewerber(id),
    enabled: !!id,
  });
}

export function useBewerberStatistics() {
  return useQuery({
    queryKey: bewerberKeys.statistics(),
    queryFn: fetchBewerberStatistics,
  });
}

export function useCreateBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBewerber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
    },
  });
}

export function useUpdateBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
      // Refresh the change history so field updates (e.g. onboarding checkboxes) show immediately.
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

export function useDeleteBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBewerber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
    },
  });
}

export function useAssignBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
      // Also invalidate timeline to show the new activity
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

export function useHasInvitationEmails(bewerberId: string) {
  return useQuery({
    queryKey: bewerberKeys.hasInvitations(bewerberId),
    queryFn: () => checkHasInvitationEmails(bewerberId),
    enabled: !!bewerberId,
  });
}

export function useBewerberDocuments(bewerberId: string) {
  return useQuery({
    queryKey: bewerberKeys.documents(bewerberId),
    queryFn: () => fetchBewerberDocuments(bewerberId),
    enabled: !!bewerberId,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.documents(variables.bewerberId) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(variables.bewerberId) });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.documents(variables.bewerberId) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(variables.bewerberId) });
    },
  });
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: downloadDocument,
  });
}

// Document preview functions
const fetchDocumentPreview = async ({
  bewerberId,
  documentId,
}: {
  bewerberId: string;
  documentId: string;
}): Promise<string> => {
  const response = await apiClient.get(
    `/bewerber/${bewerberId}/documents/${documentId}/preview`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(response.data);
};

// Hook for modal preview with TanStack Query caching
export function useDocumentPreview(
  bewerberId: string,
  documentId: string,
  enabled = false,
) {
  return useQuery({
    queryKey: bewerberKeys.documentPreview(bewerberId, documentId),
    queryFn: () => fetchDocumentPreview({ bewerberId, documentId }),
    enabled: enabled && !!bewerberId && !!documentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
  });
}

// Hook for hover preview with manual caching and cleanup
export function useLazyDocumentPreview() {
  const cache = useRef<Map<string, string>>(new Map());

  const fetchPreview = useCallback(
    async (bewerberId: string, documentId: string): Promise<string> => {
      const cacheKey = `${bewerberId}:${documentId}`;

      if (cache.current.has(cacheKey)) {
        return cache.current.get(cacheKey)!;
      }

      const url = await fetchDocumentPreview({ bewerberId, documentId });
      cache.current.set(cacheKey, url);
      return url;
    },
    [],
  );

  const clearCache = useCallback(() => {
    cache.current.forEach((url) => URL.revokeObjectURL(url));
    cache.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearCache();
  }, [clearCache]);

  return { fetchPreview, clearCache };
}

// Contract generation
export interface GenerateContractDto {
  bedarfId: string;
  startdatum: string;
}

const generateContract = async ({
  bewerberId,
  data,
}: {
  bewerberId: string;
  data: GenerateContractDto;
}): Promise<void> => {
  const response = await apiClient.post(`/bewerber/${bewerberId}/contract`, data, {
    responseType: 'blob',
  });

  // Extract filename from Content-Disposition header if available
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'Arbeitsvertrag.pdf';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
    if (filenameMatch && filenameMatch[1]) {
      filename = decodeURIComponent(filenameMatch[1]);
    }
  }

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export function useGenerateContract() {
  return useMutation({
    mutationFn: generateContract,
  });
}

// ===================
// LIFECYCLE FUNCTIONS
// ===================

const absageBewerber = async ({
  id,
  data,
}: {
  id: string;
  data: AbsageBewerberDto;
}): Promise<AbsageResponse> => {
  const response = await apiClient.post(`/bewerber/${id}/absagen`, data);
  return response.data;
};

const hardDeleteBewerber = async ({
  id,
  data,
}: {
  id: string;
  data: HardDeleteBewerberDto;
}): Promise<void> => {
  await apiClient.delete(`/bewerber/${id}/permanent`, { data });
};

const reactivateBewerber = async (id: string): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/reactivate`);
  return response.data;
};

const markAsReviewed = async (id: string): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/mark-reviewed`);
  return response.data;
};

const confirmBewerber = async (id: string): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/confirm`);
  return response.data;
};

const markNichtGeeignet = async ({
  id,
  data,
}: {
  id: string;
  data: NichtGeeignetDto;
}): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/nicht-geeignet`, data);
  return response.data;
};

// ===================
// LIFECYCLE HOOKS
// ===================

export function useAbsageBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: absageBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

export function useHardDeleteBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: hardDeleteBewerber,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
    },
  });
}

export function useReactivateBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reactivateBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

export function useMarkAsReviewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsReviewed,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

export function useConfirmBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
    },
  });
}

export function useMarkNichtGeeignet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNichtGeeignet,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

// ===================
// MARKING FUNCTIONS
// ===================

const markBewerber = async (id: string): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/mark`);
  return response.data;
};

const unmarkBewerber = async (id: string): Promise<ApiResponse<Bewerber>> => {
  const response = await apiClient.post(`/bewerber/${id}/unmark`);
  return response.data;
};

// ===================
// MARKING HOOKS
// ===================

export function useMarkBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

export function useUnmarkBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unmarkBewerber,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bewerberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.timelines(data.data.id) });
    },
  });
}

const fetchMatchingBedarfe = async (
  bewerberId: string,
  params: MatchingBedarfeParams,
): Promise<MatchingBedarfeResponse> => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });

  const query = search.toString();
  const response = await apiClient.get(
    `/bewerber/${bewerberId}/matching-bedarfe${query ? `?${query}` : ''}`,
  );
  return response.data;
};

const calculateFahrzeitToBedarf = async ({
  bewerberId,
  bedarfId,
}: {
  bewerberId: string;
  bedarfId: string;
}): Promise<ApiResponse<{ distanceKm: number; durationMinutes: number }>> => {
  const response = await apiClient.post('/geo/fahrzeit', {
    from: { kind: 'BEWERBER_HOME', id: bewerberId },
    to: { kind: 'BEDARF_SCHOOL', id: bedarfId },
  });
  return response.data;
};

/** Bedarfe within reach of one Bewerber, judged against the interview answers. */
export function useMatchingBedarfe(
  bewerberId: string,
  params: MatchingBedarfeParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: bewerberKeys.matchingBedarfe(bewerberId, params),
    queryFn: () => fetchMatchingBedarfe(bewerberId, params),
    enabled: !!bewerberId && enabled,
    // Keeps the current rows on screen while the answers are still being typed.
    placeholderData: keepPreviousData,
  });
}

export function useFahrzeitToBedarf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: calculateFahrzeitToBedarf,
    onSuccess: (_data, variables) => {
      // The list re-reads the route cache, so the new travel time shows up.
      queryClient.invalidateQueries({
        queryKey: bewerberKeys.matchingBedarfeAll(variables.bewerberId),
      });
    },
  });
}
